export type SignalLevel = 0 | 1 | 2 | 3 | 4
export type SignalBarsSize = 'sm' | 'md'

interface Props {
  level: SignalLevel
  size?: SignalBarsSize
  className?: string
}

const GEOMETRY: Record<SignalBarsSize, { bar: number; gap: number; heights: number[] }> = {
  sm: { bar: 3, gap: 2, heights: [4, 7, 10, 13] },
  md: { bar: 4, gap: 3, heights: [6, 10, 14, 18] },
}

/**
 * 4-bar signal strength indicator. Level 0 means disconnected and renders
 * all four bars dimmed with a single diagonal slash.
 */
export function SignalBars({ level, size = 'sm', className }: Props) {
  const { bar, gap, heights } = GEOMETRY[size]
  const width = bar * 4 + gap * 3 + 4
  const height = heights[heights.length - 1] + 4
  const baseY = height - 2

  return (
    <svg
      role="img"
      aria-label={level === 0 ? 'Signal lost' : `Signal ${level} of 4`}
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={className ?? 'text-gray-300 dark:text-gray-600'}
    >
      {heights.map((h, i) => {
        const filled = i < level
        return (
          <rect
            key={i}
            x={2 + i * (bar + gap)}
            y={baseY - h}
            width={bar}
            height={h}
            rx={0.5}
            fill={filled ? 'currentColor' : 'none'}
            stroke="currentColor"
            strokeWidth={filled ? 0 : 1}
            opacity={level === 0 ? 0.4 : filled ? 1 : 0.5}
          />
        )
      })}
      {level === 0 && (
        <line
          x1={1}
          y1={height - 1}
          x2={width - 1}
          y2={1}
          stroke="#ef4444"
          strokeWidth={1.5}
          strokeLinecap="round"
        />
      )}
    </svg>
  )
}

SignalBars.displayName = 'SignalBars'
